import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export function useMeasure<T extends HTMLElement>() {
  const [node, setNode] = useState<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const last = useRef({ width: 0, height: 0 });

  const ref = useCallback((el: T | null) => setNode(el), []);

  useLayoutEffect(() => {
    const w: Window | undefined = typeof window !== 'undefined' ? (window as Window) : undefined;
    if (!node || !w) return;

    let rafId = 0;
    let ro: ResizeObserver | undefined;

    const measure = () => {
      if (!node) return; // guard for detachment
      const rect = node.getBoundingClientRect();
      const next = { width: Math.round(rect.width), height: Math.round(rect.height) };
      if (next.width !== last.current.width || next.height !== last.current.height) {
        last.current = next;
        setSize(next);
      }
    };

    const queueMeasure = () => {
      w.cancelAnimationFrame(rafId);
      rafId = w.requestAnimationFrame(measure);
    };

    if ('ResizeObserver' in w) {
      ro = new (w as Window & { ResizeObserver: typeof ResizeObserver }).ResizeObserver(queueMeasure);
      ro.observe(node);
      queueMeasure();
      // eslint-disable-next-line consistent-return
      return () => {
        w.cancelAnimationFrame(rafId);
        ro?.disconnect();
      };
    }

    const onResize = () => queueMeasure();
    w.addEventListener('resize', onResize);
    w.addEventListener('orientationchange', onResize);
    queueMeasure();
    // eslint-disable-next-line consistent-return
    return () => {
      w.cancelAnimationFrame(rafId);
      w.removeEventListener('resize', onResize);
      w.removeEventListener('orientationchange', onResize);
    };
  }, [node]);

  return { ref, width: size.width, height: size.height };
}
